import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { GradeRing, GradeSteps } from '../components/GradeRing.js';
import { isGrade, useLocale } from '../components/site.js';
import { TURNSTILE_SITE_KEY, Turnstile } from '../components/Turnstile.js';
import {
  Button,
  Checkbox,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SeverityBadge,
  TextField,
  type Severity,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate, formatTime, toLanguage } from '../i18n.js';
import { useTrialDays } from '../trial.js';

/**
 * Checks per hour and IP (apps/api/src/routes/public.ts): 2 without a captcha, 5 when the third
 * and later ones are confirmed by Turnstile (audit: the number follows the captcha setting).
 */
const FREE_CHECKS_PER_HOUR = 2;
const QUICK_CHECKS_PER_HOUR = 5;

/** About how long a check takes (the copy says "etwa eine Minute"); the bar never reaches 100 %. */
const EXPECTED_MS = 60_000;

/** The five areas of a check, in the order of the result (US-09). */
const AREAS = ['availability', 'security', 'seo', 'performance', 'compliance'] as const;
const AREA_KEYS: Record<string, string> = {
  availability: 'site.tiles.availability',
  security: 'site.tiles.security',
  seo: 'site.tiles.seo',
  performance: 'site.tiles.performance',
  compliance: 'site.tiles.compliance',
};

/** Result pages are shared by link, not meant for search engines (US-09: noindex). */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
}

/**
 * Kostenloser Website-Check (design/pages/landing/quick-check.html, start): the target of the
 * landing page form (/check?url=…&start=1 starts at once, T-56).
 */
export function QuickCheckStart() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [url, setUrl] = useState(params.get('url') ?? '');
  const [token, setToken] = useState<string>();
  const [error, setError] = useState<string>();
  useNoIndex();
  const start = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/public/quick-checks', {
          body: { url: url.trim(), ...(token ? { turnstileToken: token } : {}) },
        }),
      ),
    onSuccess: (data) => void navigate(`/check/${data.id}`),
  });
  // From the landing page form (?url=…&start=1): the check starts at once, no second click (T-56).
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStarted.current || params.get('start') !== '1' || !url.trim()) return;
    autoStarted.current = true;
    start.mutate();
  }, [params, url, start]);
  const onToken = useCallback((value: string) => setToken(value), []);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return setError(t('errors.required'));
    setError(undefined);
    start.mutate();
  };
  const captcha = start.error instanceof ApiError && start.error.code === 'CAPTCHA_REQUIRED';
  let failure: string | undefined;
  if (start.error instanceof ApiError) {
    if (start.error.code === 'RATE_LIMITED') failure = t('quickCheck.limit');
    else if (!captcha) failure = errorMessage(start.error, t);
  } else if (start.error) failure = errorMessage(start.error, t);

  return (
    <section className="qc">
      <div>
        <h1>{t('quickCheck.title')}</h1>
        <p className="lead">{t('quickCheck.intro')}</p>
      </div>
      <form className="qc-form" onSubmit={submit} noValidate>
        {failure ? (
          <div className="wide">
            <Notice kind="error">{failure}</Notice>
          </div>
        ) : null}
        <TextField
          large
          label={t('quickCheck.url')}
          placeholder={t('quickCheck.urlPlaceholder')}
          inputMode="url"
          autoComplete="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          error={error}
        />
        {/* Before the button (review #178): confirm first, then send; the form stacks then. */}
        {captcha ? (
          <div className="wide">
            {TURNSTILE_SITE_KEY ? (
              <>
                <p className="mb-2">{t('quickCheck.captcha')}</p>
                <Turnstile
                  action="quick_check"
                  onToken={onToken}
                  language={i18n.language}
                  fallback={<Notice kind="warning">{t('quickCheck.limit')}</Notice>}
                />
              </>
            ) : (
              <Notice kind="warning">{t('quickCheck.limit')}</Notice>
            )}
          </div>
        ) : null}
        <Button type="submit" size="lg" busy={start.isPending}>
          {start.isPending ? t('addSite.checking') : t('quickCheck.submit')}
        </Button>
        <span className="hint">
          {t('quickCheck.privacy')}{' '}
          {t('quickCheck.perHour', {
            count: TURNSTILE_SITE_KEY ? QUICK_CHECKS_PER_HOUR : FREE_CHECKS_PER_HOUR,
          })}
        </span>
      </form>
    </section>
  );
}

const LIGHT: Record<string, [string, string]> = {
  good: ['ok', 'quickCheck.lights.good'],
  warn: ['warn', 'quickCheck.lights.warn'],
  bad: ['bad', 'quickCheck.lights.bad'],
  unknown: ['neutral', 'quickCheck.lights.unknown'],
};

function EmailForm({ id, onSent }: { id: string; onSent: (email: string) => void }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; consent?: string }>({});
  const send = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/public/quick-checks/{checkId}/email', {
          params: { path: { checkId: id } },
          body: {
            email: email.trim(),
            consent: true,
            locale: toLanguage(i18n.language),
          },
        }),
      ),
    onSuccess: () => onSent(email.trim()),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = t('errors.email');
    if (!consent) next.consent = t('quickCheck.consentRequired');
    setErrors(next);
    if (Object.keys(next).length === 0) send.mutate();
  };
  return (
    <form className="card mailform" onSubmit={submit} noValidate>
      <div>
        <h2 className="h4s">{t('quickCheck.emailTitle')}</h2>
        <p className="bodyM mt-1">{t('quickCheck.emailText')}</p>
      </div>
      {send.isError ? <Notice kind="error">{errorMessage(send.error, t)}</Notice> : null}
      <div className="row">
        <TextField
          label={t('quickCheck.email')}
          type="email"
          autoComplete="email"
          placeholder={t('auth.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />
        <Button type="submit" variant="secondary" busy={send.isPending}>
          {t('quickCheck.emailSubmit')}
        </Button>
      </div>
      <Checkbox
        label={t('quickCheck.consent')}
        checked={consent}
        onChange={(e) => setConsent(e.target.checked)}
        error={errors.consent}
      />
    </form>
  );
}

/**
 * The check of /check/:id (quick-check.html): running, the result (score, problems, the hidden
 * rest, the areas, the result by e-mail), a failed check, and "Fast geschafft" after the e-mail.
 */
export function QuickCheckResult() {
  const { checkId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const trialDays = useTrialDays();
  const [sentTo, setSentTo] = useState<string>();
  const [now, setNow] = useState(() => Date.now());
  useNoIndex();
  const check = useQuery({
    queryKey: ['quick-check', checkId, locale],
    queryFn: () =>
      unwrap(
        api.GET('/public/quick-checks/{checkId}', {
          params: { path: { checkId }, query: { locale } },
        }),
      ),
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d || d.status === 'queued' || d.status === 'running') return 2000;
      return d.result?.performancePending ? 5000 : false;
    },
  });
  const running = check.data?.status === 'queued' || check.data?.status === 'running';
  // The overall progress moves with the time since the start (the API has no progress per step).
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  if (check.isPending) return <Loading />;
  if (check.isError)
    return (
      <LoadError message={errorMessage(check.error, t)} onRetry={() => void check.refetch()} />
    );
  const c = check.data;
  // The full address travels to the registration, the app adds it after the e-mail check (T-56).
  const register = `/register?${new URLSearchParams({ url: c.url, quickCheck: c.id }).toString()}`;
  const step = (event: 'quick_check_cta_clicked' | 'quick_check_report_clicked') =>
    void api
      .POST('/public/quick-checks/{checkId}/events', {
        params: { path: { checkId: c.id } },
        body: { event },
      })
      .catch(() => undefined);

  if (running) {
    const elapsed = Math.max(0, now - new Date(c.createdAt).getTime());
    const pct = Math.min(90, Math.round((elapsed / EXPECTED_MS) * 100));
    return (
      <section className="qc">
        <div>
          <h1>{t('quickCheck.runningTitle', { host: c.host })}</h1>
          <p className="lead" role="status">
            {t('quickCheck.running')}
          </p>
        </div>
        <div
          className="progress"
          role="progressbar"
          aria-label={t('quickCheck.progress')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <i style={{ width: `${pct}%` }} />
        </div>
        <ol className="steps">
          {AREAS.map((area) => (
            <li key={area} className="run">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M12 3a9 9 0 1 0 9 9" />
              </svg>
              <span>{t(AREA_KEYS[area] ?? area)}</span>
              <span className="st">{t('onboarding.status.running')}</span>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  if (c.status === 'failed' || !c.result)
    return (
      <section className="qc">
        <div>
          <h1>{t('quickCheck.failedTitle')}</h1>
          <p className="lead">{t('quickCheck.failed', { host: c.host })}</p>
        </div>
        <Notice
          kind="warning"
          title={t('quickCheck.failedHint')}
          action={
            <LinkButton
              to={`/check?${new URLSearchParams({ url: c.url, start: '1' }).toString()}`}
              variant="primary"
              size="sm"
            >
              {t('quickCheck.again')}
            </LinkButton>
          }
        >
          {t('quickCheck.failedText')}
        </Notice>
      </section>
    );

  if (sentTo)
    return (
      <section className="qc">
        <div>
          <h1>{t('quickCheck.sentTitle')}</h1>
          <p className="lead">{t('quickCheck.sentText', { email: sentTo })}</p>
        </div>
        <Notice
          kind="info"
          title={t('quickCheck.noMail')}
          action={
            <Button variant="secondary" size="sm" onClick={() => setSentTo(undefined)}>
              {t('quickCheck.toResult')}
            </Button>
          }
        >
          {t('quickCheck.noMailText')}
        </Notice>
      </section>
    );

  const r = c.result;
  const total = r.topFindings.length + r.teaserHiddenCount;
  const checkedAt = t(r.reachable ? 'quickCheck.checkedUp' : 'quickCheck.checkedDown', {
    date: formatDate(c.createdAt, i18n.language),
    time: formatTime(c.createdAt, i18n.language),
  });
  return (
    <section className="qc result">
      <div>
        <h1>{t('quickCheck.resultTitle', { host: c.host })}</h1>
        <p className="lead">{checkedAt}</p>
      </div>
      {!r.reachable ? <Notice kind="warning">{t('quickCheck.unreachable')}</Notice> : null}
      {r.grade && isGrade(r.grade) && r.healthScore !== null ? (
        <div className="card">
          <div className="ring">
            <GradeRing score={r.healthScore} grade={r.grade} />
            <div>
              <div className="num">
                {r.healthScore}
                <small>/ 100</small>
              </div>
              <div className="cap">
                {t('onboarding.gradeCap', { grade: r.grade, label: r.label })}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <GradeSteps grade={r.grade} />
          </div>
        </div>
      ) : null}
      <div>
        <h2 className="h3s mb-3">
          {total === 0 ? t('quickCheck.top') : t('quickCheck.attention', { count: total })}
        </h2>
        {total === 0 ? <Notice kind="success">{t('quickCheck.noProblems')}</Notice> : null}
        <ol className="top">
          {r.topFindings.map((f, i) => (
            <li key={f.ruleCode}>
              <div className="top3">
                <div className="n" aria-hidden="true">
                  {i + 1}
                </div>
                <div>
                  <SeverityBadge severity={f.severity as Severity} />
                  <h3 className="h4s">{f.title}</h3>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
      {r.teaserHiddenCount > 0 ? (
        <div className="blur">
          {Array.from({ length: Math.min(r.teaserHiddenCount, 2) }, (_, i) => (
            <div key={i} className="top3" aria-hidden="true">
              <div className="n">{r.topFindings.length + i + 1}</div>
              <div className="grid gap-2">
                <div className="skel w-1/4" />
                <div className="skel w-3/4" />
                <div className="skel w-1/2" />
              </div>
            </div>
          ))}
          <div className="lock">
            <p>{t('quickCheck.more', { count: r.teaserHiddenCount })}</p>
          </div>
        </div>
      ) : null}
      <div className="ctas">
        <LinkButton
          to={register}
          variant="primary"
          size="lg"
          onClick={() => step('quick_check_cta_clicked')}
        >
          {t('quickCheck.cta')}
        </LinkButton>
        <LinkButton
          to={register}
          variant="secondary"
          size="lg"
          onClick={() => step('quick_check_report_clicked')}
        >
          {t('quickCheck.reportCta')}
        </LinkButton>
        {trialDays !== undefined ? (
          <span className="hint">{t('quickCheck.noCard', { count: trialDays })}</span>
        ) : null}
      </div>
      <div>
        <h2 className="h3s mb-3">{t('quickCheck.areas')}</h2>
        <ul className="lights">
          {r.categories.map((cat) => {
            const [tone, label] =
              cat.key === 'performance' && r.performancePending
                ? ['neutral', 'quickCheck.pending']
                : // PSI gave no answer: the speed was not measured, not "not checked" (SP-A2.44).
                  cat.key === 'performance' && cat.status === 'unknown'
                  ? ['neutral', 'quickCheck.lights.notMeasured']
                  : (LIGHT[cat.status] ?? ['neutral', 'quickCheck.lights.unknown']);
            return (
              <li key={cat.key} className="light">
                <span className={`badge ${tone}`}>{t(label)}</span>
                <b>{t(AREA_KEYS[cat.key] ?? cat.key)}</b>
              </li>
            );
          })}
        </ul>
      </div>
      <EmailForm id={c.id} onSent={setSentTo} />
    </section>
  );
}

/** Link from the double opt-in mail. */
export function QuickCheckConfirm() {
  const { checkId = '' } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  useNoIndex();
  const confirm = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/public/quick-checks/{checkId}/email/confirm', {
          params: { path: { checkId } },
          body: { token: params.get('token') ?? '' },
        }),
      ),
  });
  const { mutate } = confirm;
  // Once: the token works only one time (StrictMode runs effects twice in development).
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    mutate();
  }, [mutate]);
  return (
    <section className="qc">
      <h1>{t('quickCheck.confirmTitle')}</h1>
      {confirm.isPending || confirm.isIdle ? <Loading /> : null}
      {confirm.isSuccess ? <Notice kind="success">{t('quickCheck.confirmed')}</Notice> : null}
      {confirm.isError ? <Notice kind="error">{errorMessage(confirm.error, t)}</Notice> : null}
      <div>
        <LinkButton to={`/check/${checkId}`} variant="secondary">
          {t('quickCheck.toResult')}
        </LinkButton>
      </div>
    </section>
  );
}
